import React, { CSSProperties, ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import useDeviceSize from '../../../util/Hooks/useDeviceSize';
import { MediaComponent, StrapiImageComponent } from '../../../util/MediaComponent/MediaComponent';
import LargeWidthContainer from '../LargeWidthContainer';
import MediumWidthContainer from '../MediumWidthContainer';
import styles from './imageAndTextContainer.module.scss';
import { StrapiImage, ImageProps } from '../../../Models/MediaProps';

interface ImageAndTextProps {
    image?: ImageProps;
    strapiImage?: StrapiImage;
    heading?: string;
    h1?: boolean;
    ingressSize?: 'small' | 'big';
    paragraphSize?: 'small' | 'big';
    ingress?: string;
    markdown?: string;
    children?: ReactNode;
    pictureFirstOrLast?: 'first' | 'last';
    largeContainer?: boolean;
    extraClassNames?: string;
    extraStyles?: CSSProperties;
    isVideo?: boolean;
    isInvertedOnMobile?: boolean;
    isIngressTop?: boolean;
}

const ImageAndTextContainer = ({
    image,
    strapiImage,
    h1 = false,
    ingressSize = 'small',
    ingress,
    paragraphSize = 'small',
    markdown,
    children,
    pictureFirstOrLast = 'first',
    largeContainer = false,
    extraClassNames,
    extraStyles,
    isVideo = false,
    isInvertedOnMobile = false,
    isIngressTop = false,
}: ImageAndTextProps) => {
    const [widthScreen, heightScreen] = useDeviceSize();

    const pictureLast = pictureFirstOrLast === 'last' ? styles.pictureLast : '';
    let padding: any;

    if (largeContainer) {
        padding =
            pictureFirstOrLast === 'last' ? styles.paddingRightLarge : styles.paddingLeftLarge;
    } else {
        padding = pictureFirstOrLast === 'last' ? styles.paddingRight : styles.paddingLeft;
    }
    const ingressTitle = ingressSize === 'small' ? styles.ingressSmall : styles.ingressBig;
    const paragraph = paragraphSize === 'small' ? styles.paragraphSmall : styles.paragraphBig;

    const renderIngress = () => {
        if (h1) {
            return <h1 className={ingressTitle}>{ingress}</h1>;
        } else {
            return <h2 className={ingressTitle}>{ingress}</h2>;
        }
    };

    const renderImage = () => {
        if (strapiImage) {
            return (
                <StrapiImageComponent
                    maxWidth={strapiImage.width}
                    maxHeight={strapiImage.height}
                    strapiImage={strapiImage}
                />
            );
        } else {
            if (image?.src) {
                return (
                    <MediaComponent
                        maxWidth={image.maxWidth}
                        maxHeight={image.maxHeight}
                        src={image.src}
                        alt={image.alt}
                        isVideo={isVideo}
                    />
                );
            } else {
                return null;
            }
        }
    };

    const renderContent = () => {
        if (widthScreen < 768) {
            if (isInvertedOnMobile) {
                return (
                    <div className={`${styles.imageAndTextContainer} ${pictureLast}`}>
                        <div className={`${styles.textContainer} ${padding}`}>
                            {ingress ? renderIngress() : null}
                            {children ? <div className={paragraph}>{children}</div> : null}
                            {markdown ? (
                                <ReactMarkdown className={paragraph}>{markdown}</ReactMarkdown>
                            ) : null}
                        </div>
                        <div className={styles.imageContainer}>{renderImage()}</div>
                    </div>
                );
            } else if (isIngressTop)
                return (
                    <div className={`${styles.imageAndTextContainer} ${pictureLast}`}>
                        <div className={`${styles.textContainer} ${padding}`}>
                            {ingress ? renderIngress() : null}
                            <div className={styles.imageContainer}>{renderImage()}</div>
                            {children ? <div className={paragraph}>{children}</div> : null}
                            {markdown ? (
                                <ReactMarkdown className={paragraph}>{markdown}</ReactMarkdown>
                            ) : null}
                        </div>
                    </div>
                );
        }

        return (
            <div className={`${styles.imageAndTextContainer} ${pictureLast}`}>
                <div className={styles.imageContainer}>{renderImage()}</div>
                <div className={`${styles.textContainer} ${padding}`}>
                    {ingress ? renderIngress() : null}
                    {children ? <div className={paragraph}>{children}</div> : null}
                    {markdown ? (
                        <ReactMarkdown className={paragraph}>{markdown}</ReactMarkdown>
                    ) : null}
                </div>
            </div>
        );
    };

    if (largeContainer) {
        return (
            <LargeWidthContainer extraClassNames={extraClassNames} extraStyles={extraStyles}>
                {renderContent()}
            </LargeWidthContainer>
        );
    } else {
        return (
            <MediumWidthContainer extraClassNames={extraClassNames} extraStyles={extraStyles}>
                {renderContent()}
            </MediumWidthContainer>
        );
    }
};

export default ImageAndTextContainer;
